import React from 'react';
import { motion } from 'framer-motion';
import { Users, User, Target } from 'lucide-react';

const WHOP_URL = 'https://whop.com/joined/thedillonblueprint/';

const SMALL_GROUP_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/vjw8o0ts_Small%20Group.webp';
const PRIVATE_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/agf340za_Private%20Training.webp';
const MENTOR_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/jb8pnlsa_Mentor%20Photo.webp';

const services = [
  {
    icon: Users,
    title: 'Small Group Training',
    copy: 'Competitive skill-development sessions focused on shooting mechanics, decision-making, and game-like situations.',
    image: SMALL_GROUP_IMG,
    cta: 'Join Now',
    link: WHOP_URL,
  },
  {
    icon: User,
    title: 'Private Training',
    copy: "Individualized sessions built around the player's mechanics, weaknesses, goals, and overall development.",
    image: PRIVATE_IMG,
    cta: 'Join Now',
    link: WHOP_URL,
  },
  {
    icon: Target,
    title: 'Player Mentorship',
    copy: 'Accountability, progress tracking, in-game and off-season goals, and film study to help players better understand their game.',
    image: MENTOR_IMG,
    cta: 'Join Now',
    link: WHOP_URL,
  },
];

const BasketballIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9.5" />
    <path d="M2.5 12h19M12 2.5v19M5 5c3 2.5 5 5 5 7s-2 4.5-5 7M19 5c-3 2.5-5 5-5 7s2 4.5 5 7" />
  </svg>
);

export const BasketballServices = () => {
  return (
    <section
      id="services"
      data-testid="basketball-services"
      className="bg-black py-16 sm:py-14 sm:py-16 lg:py-20 lg:py-24 px-6 border-y border-neutral-900"
    >
      <div className="container mx-auto max-w-6xl">
        {/* Header with basketball divider */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="services-heading"
            className="font-display text-white text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight"
          >
            Services
          </h2>
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-700 to-neutral-700 w-32 sm:w-48" />
            <BasketballIcon />
            <div className="h-px bg-gradient-to-l from-transparent via-neutral-700 to-neutral-700 w-32 sm:w-48" />
          </div>
        </motion.div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`service-card-${i}`}
              className="group relative rounded-2xl bg-neutral-950 border border-neutral-800/70 overflow-hidden flex flex-col card-hover-lift spotlight hover:border-neutral-600 transition-all duration-500"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
              }}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <s.icon className="w-6 h-6 text-gray-300" strokeWidth={1.5} />
                  <h3 className="font-display text-white text-xl uppercase tracking-wide">
                    {s.title}
                  </h3>
                </div>

                <div className="h-px bg-neutral-800 mb-5" />

                <p className="text-gray-400 text-sm leading-relaxed mb-8">
                  {s.copy}
                </p>

                <a
                  href={s.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-card-${i}-cta`}
                  className="mt-auto inline-flex items-center justify-center text-center font-bold tracking-wider text-xs uppercase px-5 py-3 rounded-lg bg-white text-black hover:bg-gray-200 transition-colors"
                >
                  {s.cta}
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer caption */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          data-testid="services-footer-caption"
          className="text-center text-gray-500 text-sm mt-14 max-w-2xl mx-auto"
        >
          Training and mentorship options are available for players at different stages of development.
        </motion.p>
      </div>
    </section>
  );
};
